import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Alert, Button, LoadingSpinner, ReportDateRangePicker, Tabs } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { LuRefreshCw, LuDownload } from 'react-icons/lu';

import FilterSidebar from './FilterSidebar';
import OverviewTab from './OverviewTab';
import ObjectiveScorecard from './objectives/ObjectiveScorecard';
import ScheduleSummaryTab from './objectives/ScheduleSummaryTab';
import OeSummaryTab from './objectives/OeSummaryTab';
import JafEstTab from './objectives/JafEstTab';
import { OBJECTIVE_TAB_KEYS } from './objectives/scorecard';

import {
  fetchChangeOrderSummary,
  fetchStatusDistribution,
  fetchCSEStatistics,
  fetchShopDrawingAnalysis,
  fetchFilterOptions,
  fetchDetailedChangeOrders,
  fetchSalesUnitStatistics,
  fetchObjectives,
  exportDetailedChangeOrders,
  exportObjectives,
  resetError
} from 'redux/crm/dashboard/changeOrderSlice';

const DEFAULT_START = dayjs().startOf('year').format('YYYY-MM-DD');
const DEFAULT_END   = dayjs().format('YYYY-MM-DD');
const LOAD_TIMEOUT_MS = 30000; // 30 seconds
// The sidebar applies filters live (Job # on every keystroke); the objective
// roll-up is the heaviest request on the page, so it waits for a pause.
const OBJECTIVE_DEBOUNCE_MS = 500;

// Tab order = reading order for a manager: volume first, then the three
// objectives (Obj.2 schedule, Obj.3 order entry, Obj.1 JAF vs EST). The key is
// what lands in `?tab=` so a link opens the same view.
const TABS = [
  { key: 'overview',                    label: 'Overview' },
  { key: OBJECTIVE_TAB_KEYS.schedule,   label: 'Schedule Performance', Component: ScheduleSummaryTab },
  { key: OBJECTIVE_TAB_KEYS.orderEntry, label: 'Order Entry',          Component: OeSummaryTab },
  { key: OBJECTIVE_TAB_KEYS.jafEst,     label: 'JAF vs EST',           Component: JafEstTab },
];
const tabIndexOf = (key) => Math.max(0, TABS.findIndex((tab) => tab.key === key));

const toUrlParams = (dateRange, filters, tabKey) => ({
  start_date: dateRange.startDate,
  end_date:   dateRange.endDate,
  ...(filters.building_statuses.length > 0 && { building_statuses: filters.building_statuses.join(',') }),
  ...(filters.co_statuses.length > 0        && { co_statuses:       filters.co_statuses.join(',')       }),
  ...(filters.sales_units.length > 0        && { sales_units:       filters.sales_units.join(',')       }),
  ...(filters.cse_heads?.length > 0         && { cse_heads:         filters.cse_heads.join(',')         }),
  ...(filters.job_number                    && { job_number:         filters.job_number                  }),
  ...(tabKey !== TABS[0].key                && { tab:                tabKey                              }),
});

const ChangeOrderDashboard = () => {
  const dispatch       = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pageReady, setPageReady] = useState(false);
  const [timeoutOccurred, setTimeoutOccurred] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('start_date') || DEFAULT_START,
    endDate:   searchParams.get('end_date')   || DEFAULT_END
  });

  const [filters, setFilters] = useState({
    building_statuses: searchParams.get('building_statuses')?.split(',').filter(Boolean) || [],
    co_statuses:       searchParams.get('co_statuses')?.split(',').filter(Boolean)       || [],
    sales_units:       searchParams.get('sales_units')?.split(',').filter(Boolean)       || [],
    cse_heads:         searchParams.get('cse_heads')?.split(',').filter(Boolean)         || [],
    job_number:        searchParams.get('job_number') || ''
  });

  const [detailedPageNo, setDetailedPageNo] = useState(1);
  const [activeTab, setActiveTab] = useState(() => tabIndexOf(searchParams.get('tab')));
  const [countLabels, setCountLabels] = useState({});

  // One stable reporter per tab: the objective tabs list it as an effect dep.
  const reporters = useMemo(
    () => TABS.map((_, index) => (label) =>
      setCountLabels((prev) => (prev[index] === label ? prev : { ...prev, [index]: label }))),
    []
  );

  const dashboardState = useSelector(state => state.crm_dashboard_change_order);
  const { summary, filterOptions, objectives, objectivesExport, error } = dashboardState;

  const buildParams = useCallback((pageNo) => ({
    start_date:  dateRange.startDate,
    end_date:    dateRange.endDate,
    ...filters,
    pagination:  { page: pageNo, per_page: 50 }
  }), [dateRange, filters]);

  const baseParams = useMemo(() => buildParams(1), [buildParams]);

  // Objectives take the sidebar filters but no paging — one roll-up per scope.
  const objectiveParams = useMemo(() => ({
    start_date: dateRange.startDate,
    end_date:   dateRange.endDate,
    ...filters
  }), [dateRange, filters]);

  // Fetch all data
  useEffect(() => {
    dispatch(fetchChangeOrderSummary(baseParams));
    dispatch(fetchStatusDistribution(baseParams));
    dispatch(fetchCSEStatistics(baseParams));
    dispatch(fetchShopDrawingAnalysis(baseParams));
    dispatch(fetchFilterOptions(baseParams));
    dispatch(fetchSalesUnitStatistics(baseParams));
  }, [dispatch, baseParams]);

  useEffect(() => {
    dispatch(fetchDetailedChangeOrders(buildParams(detailedPageNo)));
  }, [dispatch, buildParams, detailedPageNo]);

  // The scorecard sits above every tab, so objectives load with the page.
  // A newer scope aborts the request still in flight.
  useEffect(() => {
    let request = null;
    const timer = setTimeout(() => {
      request = dispatch(fetchObjectives(objectiveParams));
    }, OBJECTIVE_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      request?.abort();
    };
  }, [dispatch, objectiveParams]);

  useEffect(() => {
    setSearchParams(toUrlParams(dateRange, filters, TABS[activeTab].key), { replace: true });
  }, [dateRange, filters, activeTab, setSearchParams]);

  const handleRefresh = () => {
    dispatch(resetError());
    dispatch(fetchChangeOrderSummary(baseParams));
    dispatch(fetchStatusDistribution(baseParams));
    dispatch(fetchCSEStatistics(baseParams));
    dispatch(fetchShopDrawingAnalysis(baseParams));
    dispatch(fetchFilterOptions(baseParams));
    dispatch(fetchSalesUnitStatistics(baseParams));
    dispatch(fetchDetailedChangeOrders(buildParams(detailedPageNo)));
    dispatch(fetchObjectives(objectiveParams));
  };

  const handleDateRangeChange = ({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
    setDetailedPageNo(1);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setDetailedPageNo(1);
  };

  const handleResetFilters = () => {
    setFilters({ building_statuses: [], co_statuses: [], sales_units: [], cse_heads: [], job_number: '' });
    setDetailedPageNo(1);
  };

  const handleRequestExport = useCallback(() => {
    return dispatch(exportDetailedChangeOrders({
      start_date: dateRange.startDate,
      end_date:   dateRange.endDate,
      ...filters
    }));
  }, [dispatch, dateRange, filters]);

  const handleExportObjectives = useCallback(() => {
    dispatch(exportObjectives(objectiveParams));
  }, [dispatch, objectiveParams]);

  const handleSelectObjective = useCallback((tabKey) => setActiveTab(tabIndexOf(tabKey)), []);

  const handleJobNumberChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, job_number: value }));
    setDetailedPageNo(1);
  }, []);

  const activeFilterCount =
    filters.building_statuses.length +
    filters.co_statuses.length +
    filters.sales_units.length +
    (filters.cse_heads?.length || 0) +
    (filters.job_number ? 1 : 0);

  const tabs = useMemo(() => TABS.map(({ key, label, Component }, index) => ({
    label,
    content: Component ? (
      <Component
        active={activeTab === index}
        onTotalsChange={reporters[index]}
        objectiveData={objectives.data}
        objectiveLoading={objectives.loading}
        objectiveError={objectives.error}
      />
    ) : (
      <OverviewTab
        state={dashboardState}
        onPageChange={setDetailedPageNo}
        onRequestExport={handleRequestExport}
        jobNumber={filters.job_number || ''}
        onJobNumberChange={handleJobNumberChange}
      />
    ),
    key,
  })), [activeTab, reporters, objectives, dashboardState, handleRequestExport, filters.job_number, handleJobNumberChange]);

  const onObjectiveTab = Boolean(TABS[activeTab].Component);

  // Mark page as ready once the first summary response arrives (data or error).
  useEffect(() => {
    if (!summary.loading && (summary.data !== null || summary.error)) {
      setPageReady(true);
    }
  }, [summary.loading, summary.data, summary.error]);

  // Timeout detection: fire if no data after LOAD_TIMEOUT_MS
  useEffect(() => {
    if (pageReady) return; // Already loaded, clear timeout

    const timeoutId = setTimeout(() => {
      setTimeoutOccurred(true);
    }, LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [pageReady]);

  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" message="Loading Change Order Dashboard…" />
        {timeoutOccurred && (
          <Alert variant="warning" className="tw-max-w-sm">
            <p className="tw-font-semibold tw-text-sm tw-mb-2">Dashboard is taking longer than expected</p>
            <p className="tw-text-xs tw-text-gray-700 tw-mb-3">The dashboard is still loading. This may indicate a slow connection or a temporary server issue.</p>
            <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </Alert>
        )}
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      {/* ── Sticky Header ── */}
      <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-white tw-border-b tw-border-gray-200 tw-shadow-sm">
        <div className="tw-px-6 tw-py-4">
          <div className="tw-flex tw-flex-col lg:tw-flex-row lg:tw-items-center tw-gap-4">
            {/* Title */}
            <div className="tw-flex-shrink-0">
              <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900">Change Order Dashboard</h1>
              <p className="tw-text-gray-500 tw-text-sm tw-mt-0.5">
                Track and analyze change orders across projects
              </p>
            </div>

            {/* Date range picker */}
            <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
              <ReportDateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={handleDateRangeChange}
              />

              {activeFilterCount > 0 && (
                <span className="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-bg-blue-100 tw-text-blue-800">
                  {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
                </span>
              )}
            </div>

            {/* Action buttons */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
              {onObjectiveTab && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="tw-flex tw-items-center tw-gap-1.5"
                  onClick={handleExportObjectives}
                  disabled={objectivesExport.loading}
                >
                  <LuDownload size={14} />
                  {objectivesExport.loading ? 'Exporting…' : 'Export objectives'}
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                className="tw-flex tw-items-center tw-gap-1.5"
                onClick={handleRefresh}
              >
                <LuRefreshCw size={14} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div className="tw-px-6 tw-pt-4">
          <Alert variant="error">
            <p className="tw-font-semibold tw-text-sm">{error}</p>
            <p className="tw-text-xs tw-text-gray-600 tw-mt-1">Please refresh or contact support.</p>
          </Alert>
        </div>
      )}

      {/* ── Main Layout ── */}
      <div className="tw-p-6">
        <div className="tw-flex tw-gap-6">
          {/* Sidebar */}
          <div className="tw-w-56 tw-flex-shrink-0 tw-hidden lg:tw-block">
            <div className="tw-sticky" style={{ top: '80px' }}>
              <FilterSidebar
                filterOptions={filterOptions.data}
                currFilters={filters}
                onFilterChange={(key, value) =>
                  setFilters(prev => ({ ...prev, [key]: value }))
                }
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                loading={filterOptions.loading}
              />
            </div>
          </div>

          {/* Mobile filter button (shown on mobile only) */}
          <div className="tw-lg:tw-hidden tw-fixed tw-bottom-6 tw-right-6 tw-z-40 lg:tw-hidden">
            <FilterSidebar
              filterOptions={filterOptions.data}
              currFilters={filters}
              onFilterChange={(key, value) =>
                setFilters(prev => ({ ...prev, [key]: value }))
              }
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              loading={filterOptions.loading}
            />
          </div>

          {/* Content */}
          <div className="tw-flex-1 tw-min-w-0 tw-space-y-6">
            <ObjectiveScorecard
              data={objectives.data}
              loading={objectives.loading}
              error={objectives.error}
              onSelect={handleSelectObjective}
            />

            <div className="tw-relative">
              {onObjectiveTab && countLabels[activeTab] && (
                <span className="tw-absolute tw-right-0 tw-top-3 tw-hidden md:tw-block tw-text-xs tw-font-medium tw-text-slate-400">
                  {countLabels[activeTab]}
                </span>
              )}
              <Tabs
                variant="underline"
                lazyMount
                activeTab={activeTab}
                onChange={setActiveTab}
                contentClassName="tw-pt-4"
                tabs={tabs}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeOrderDashboard;
